import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { QcResponseGrid } from 'src/app/shared/models/grid.model';
import { IS_SEARCH } from 'src/app/shared/models/load.models';
import { EnvironmentFacadeService } from 'src/app/shared/services/environment.facade.service';
import { PaginatorLiteralsFacadeService } from 'src/app/shared/services/literals/paginator.literals.facade.service';
import { RecipesLiteralsFacadeService } from 'src/app/shared/services/literals/recipes.literals.facade.service.';
import { PaginatorFacadeService } from 'src/app/shared/services/paginator.facade.service';
import { PointSource } from '../models/registration-points.model';
import { SearchRecipesFacadeService } from './search.facade.service';

@Injectable({
  providedIn: 'root',
})
export class SearchRecipesService {
  constructor(
    private http: HttpClient,
    private environmentFacadeService: EnvironmentFacadeService,
    private searchFacade: SearchRecipesFacadeService,
    private paginatorFacade: PaginatorFacadeService,
    private paginatorLiterals: PaginatorLiteralsFacadeService,
    private recipeLiterals: RecipesLiteralsFacadeService,
  ) {}

  public getRecipeTypes(): Observable<any> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/flow-recipes/types';
    return this.http.get<any>(url, { withCredentials: true });
  }

  public getRecipeCodes(): Observable<any> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/flow-recipes/codes';
    return this.http.get<any>(url, { withCredentials: true });
  }

  public getSchemas(): Observable<any> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/loyalty/schemas';
    return this.http.get<any>(url, { withCredentials: true });
  }

  public getCountries(): Observable<any> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/countries';
    return this.http.get<any>(url, { withCredentials: true });
  }

  public getRecipeByCode(recipeCode: string): Observable<any> {
    const url =
      this.environmentFacadeService.getEnvironment() + `/rest/v1/flow-recipes/${recipeCode}`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  public getPointSources(): Observable<PointSource[]> {
    const url =
      this.environmentFacadeService.getEnvironment() +
      '/rest/v1/loyalty/lookup-properties/pointsourches';
    return this.http.get<PointSource[]>(url, { withCredentials: true });
  }

  public searchRecipes(): Observable<any> {
    const url =
      this.environmentFacadeService.getEnvironment() + '/rest/v1/flow-recipes/findByFilter';
    const payload = this.constructSearchDTO();
    return this.http.post<any>(url, payload, {
      withCredentials: true,
      context: new HttpContext().set(IS_SEARCH, true),
    });
  }

  public searchRecipesCount(): Observable<QcResponseGrid> {
    const url =
      this.environmentFacadeService.getEnvironment() + '/rest/v1/flow-recipes/countByFilter';
    const payload = this.constructSearchDTO();

    return this.http.post<QcResponseGrid>(url, payload, {
      withCredentials: true,
      context: new HttpContext().set(IS_SEARCH, true),
    });
  }

  public deactivateRecipe(recipeCode: string) {
    const url =
      this.environmentFacadeService.getEnvironment() + `/rest/v1/flow-recipes/${recipeCode}`;
    return this.http.delete(url, { withCredentials: true });
  }

  public activateRecipe(recipe: any) {
    const url =
      this.environmentFacadeService.getEnvironment() + `/rest/v1/flow-recipes/${recipe.recipeCode}`;
    const payload = this.constructActivateRecipeObject(recipe);
    return this.http.put(url, payload, { withCredentials: true });
  }

  private constructActivateRecipeObject(recipe: any) {
    const payload = {
      deleteFlag: false,
      recipeType: recipe.recipeType,
      recipeDisplayName: recipe.recipeDisplayName,
      recipeCode: recipe.recipeDisplayName,
      loyaltySchemaCode: recipe.loyaltySchemaCode,
      pointsToReward: recipe.pointsToReward,
      startDate: recipe.startDate,
      endDate: recipe.endDate,
      remark: recipe.remark,
      loyaltyPointSourceId: recipe.loyaltyPointSourceId,
      pointExpirationType: recipe.pointExpirationType,
      pointExpirationValue: recipe.pointExpirationValue,
      description: recipe.description,
    };
    return payload;
  }

  private constructSearchDTO() {
    const sortMapping: { [key: string]: string } = {
      [this.recipeLiterals.getCreationDate]: 'CREATION_DATE',
      [this.recipeLiterals.getId]: 'ID',
      [this.recipeLiterals.getChangeDate]: 'CHANGE_DATE',
    };
    const payload = {
      recipeType: this.searchFacade.selectedRecipeType,
      recipeCode: this.searchFacade.selectedRecipeName,
      active: this.searchFacade.active ? true : this.searchFacade.deleted ? false : null,
      startDate: this.searchFacade.selectedStartDateString,
      endDate: this.searchFacade.selectedEndDateString,
      pagination: {
        page: this.paginatorFacade.pageIndex,
        size: this.paginatorFacade.itemsNumber,
      },
      sorting: {
        sortBy: sortMapping[this.paginatorFacade.selectedSortField] || null,
        sortOrder:
          this.paginatorFacade.selectedOrder === this.paginatorLiterals.orderAsc ? 'ASC' : 'DESC',
      },
    };

    return payload;
  }
}
